/**
 * Speaker Notes read/write via Office.js PowerPoint host.
 *
 * getSpeakerNotes/setSpeakerNotes read and write speaker notes via an OOXML
 * export/rewrite/reinsert round-trip rather than a PowerPoint JS API notes
 * property — the PowerPoint JS API has no notes-related surface at all (no
 * `Slide.notesPage`, checked against @types/office-js). This mirrors the
 * proven export -> rewrite -> insertSlidesFromBase64 -> delete-original-by-id
 * pattern already used for brand-check auto-fixes (see brandFixer.ts's
 * fixIssuesViaOoxml) — replaceSlideWithRewrittenCopy itself lives in
 * brandApplier, shared by both.
 *
 * The generic Office.js host helpers below (hasPpt/ppt/getSelectedSlide/
 * runPpt/logPptFailure) are kept local to this file rather than shared with
 * integrations/powerpoint/host/slideOps — that folder belongs to a separate,
 * unrelated set of PowerPoint features and isn't a dependency this feature
 * should take on.
 */
import { strFromU8, strToU8 } from "fflate";
import { logError, scrubMessage } from "../../../../../telemetry";
import {
  unzipPptxBase64,
  replaceSlideWithRewrittenCopy,
  type PptxZip,
} from "../../apply-brand/services/brandApplier";
import { resolveRelationshipTarget } from "../../../utils/ooxmlRelationships";

function hasPpt(): boolean {
  return (globalThis as { PowerPoint?: unknown }).PowerPoint !== undefined;
}

function ppt(): typeof PowerPoint {
  return (globalThis as { PowerPoint: typeof PowerPoint }).PowerPoint;
}

/** Load and return the currently selected slide (first item), or undefined if none is selected. */
async function getSelectedSlide(
  context: PowerPoint.RequestContext,
  loadOption: string,
): Promise<PowerPoint.Slide | undefined> {
  const selected = context.presentation.getSelectedSlides();
  selected.load(loadOption);
  await context.sync();
  return selected.items[0];
}

/**
 * Run `task` inside a PowerPoint.run batch, returning `defaultValue` when the
 * host isn't PowerPoint or the batch throws/rejects.
 */
async function runPpt<T>(
  defaultValue: T,
  task: (context: PowerPoint.RequestContext) => Promise<T>,
): Promise<T> {
  if (!hasPpt()) return defaultValue;
  try {
    return await new Promise<T>((resolve) => {
      ppt()
        .run(async (context) => resolve(await task(context)))
        .catch(() => resolve(defaultValue));
    });
  } catch {
    return defaultValue;
  }
}

/** Report a tagged PowerPoint host failure via telemetry (never `console.*`) and return the `false` these helpers fail with. */
function logPptFailure(reason: string, error: unknown): false {
  logError(
    "error.office-js",
    {
      type: error instanceof Error ? error.name : "PowerPointHostError",
      scrubbedMessage: scrubMessage(error instanceof Error ? error.message : String(error)),
    },
    { reason },
  );
  return false;
}

const NS_P = "http://schemas.openxmlformats.org/presentationml/2006/main";
const NS_A = "http://schemas.openxmlformats.org/drawingml/2006/main";
// Auto-populated notes placeholders (date/slide-number/footer/header) are
// never real user-authored speaker notes — skip them when reading, and never
// disturb them when writing.
const AUTO_PLACEHOLDER_TYPES = new Set(["dt", "sldNum", "ftr", "hdr"]);

/** Read a zip entry's contents as a UTF-8 string, or null when the path is absent. */
function readZipText(files: PptxZip, path: string): string | null {
  const bytes = files[path];
  return bytes ? strFromU8(bytes) : null;
}

const XML_PROLOG = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`;

/**
 * Serialize a parsed XML Document back to text, restoring the XML prolog if
 * missing. Whether XMLSerializer re-emits the original document's `<?xml ...?>`
 * declaration is implementation-dependent — some engines strip it, others
 * (observed in the real PowerPoint desktop host, unlike jsdom in tests) keep
 * it — so this checks rather than unconditionally prepending, to avoid ever
 * producing a document with two prologs (which PowerPoint's insert rejects
 * outright with RichApi.Error: InvalidArgument).
 */
function serializeXmlDocument(doc: Document): string {
  const serialized = new XMLSerializer().serializeToString(doc);
  return serialized.startsWith("<?xml") ? serialized : XML_PROLOG + serialized;
}

/**
 * Parse XML text for in-place editing, or null (logging under `reason`) if
 * it isn't well-formed. Used by the write paths that merge a new element
 * into an existing OOXML part rather than generating one from scratch —
 * parsing (and checking the whole document, not just one substring) is what
 * lets addNotesSlideRelationship/ensureNotesSlideContentType below build the
 * new element with the DOM API instead of splicing raw strings together.
 */
function parseXmlForEditing(xml: string, reason: string, partDescription: string): Document | null {
  const doc = new DOMParser().parseFromString(xml, "text/xml");
  if (doc.getElementsByTagName("parsererror").length > 0 || !doc.documentElement) {
    logPptFailure(reason, new Error(`${partDescription} is not well-formed XML`));
    return null;
  }
  return doc;
}

const SLIDE_PART_DIR = "ppt/slides";
const SLIDE1_RELS_PATH = `${SLIDE_PART_DIR}/_rels/slide1.xml.rels`;
const NOTES_SLIDE_RELATIONSHIP_TYPE_SUFFIX = "/notesSlide";

/** Resolve the notesSlide part path linked from a single-slide export's slide1.xml.rels. */
function resolveNotesPath(relsXml: string | null): string | null {
  return resolveRelationshipTarget(relsXml, NOTES_SLIDE_RELATIONSHIP_TYPE_SUFFIX, SLIDE_PART_DIR);
}

/** True for auto-populated placeholders (date/slide-number/footer/header) — never real speaker notes. */
function isAutoPlaceholderShape(sp: Element): boolean {
  const ph = sp
    .getElementsByTagNameNS(NS_P, "nvSpPr")[0]
    ?.getElementsByTagNameNS(NS_P, "nvPr")[0]
    ?.getElementsByTagNameNS(NS_P, "ph")[0];
  const phType = ph?.getAttribute("type");
  return Boolean(phType && AUTO_PLACEHOLDER_TYPES.has(phType));
}

/**
 * Concatenate the text runs (`a:t`) of a single paragraph (`a:p`). Only
 * reads standard read-only DOM properties (getElementsByTagNameNS,
 * textContent) on an already-parsed Element, neither of which throws —
 * no try/catch needed here.
 */
function extractParagraphText(p: Element): string {
  let text = "";
  for (const t of p.getElementsByTagNameNS(NS_A, "t")) {
    text += t.textContent ?? "";
  }
  return text;
}

/** Collect the non-empty paragraph lines of a shape's txBody, or [] if it has none. */
function extractShapeNotesLines(sp: Element): string[] {
  // A shape's text body is normally `p:txBody`, but PowerPoint has also been
  // observed emitting `a:txBody` (drawingml namespace) for some shape types
  // — check both so real notes aren't silently missed.
  const txBody = sp.getElementsByTagNameNS(NS_P, "txBody")[0] ?? sp.getElementsByTagNameNS(NS_A, "txBody")[0];
  if (!txBody) {
    return [];
  }

  const lines: string[] = [];
  for (const p of txBody.getElementsByTagNameNS(NS_A, "p")) {
    const text = extractParagraphText(p);
    if (text.trim()) {
      lines.push(text);
    }
  }
  return lines;
}

/**
 * Extract the plain-text speaker notes from an exported single-slide pptx
 * zip. Exported for reuse by brand-check's "Notes present" hygiene scan,
 * which needs the same auto-placeholder-excluding extraction rather than a
 * naive "any `<a:t>` text" check (which would false-positive on cached
 * slide-number/date field text).
 */
export function extractNotesText(files: PptxZip): string {
  const notesPath = resolveNotesPath(readZipText(files, SLIDE1_RELS_PATH));
  const notesXml = notesPath ? readZipText(files, notesPath) : null;
  if (!notesXml) return "";

  const notesDoc = new DOMParser().parseFromString(notesXml, "text/xml");
  if (notesDoc.getElementsByTagName("parsererror").length > 0) return "";

  const paragraphs: string[] = [];
  for (const sp of notesDoc.getElementsByTagNameNS(NS_P, "sp")) {
    if (isAutoPlaceholderShape(sp)) continue;
    const lines = extractShapeNotesLines(sp);
    if (lines.length > 0) paragraphs.push(lines.join("\n"));
  }
  return paragraphs.join("\n\n").trim();
}

function escapeXmlText(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Field id for the slide-number placeholder's <a:fld> — an arbitrary but stable GUID, matching the reference prototype's notes-slide XML. */
const SLIDE_NUM_FIELD_ID = "{B6F15528-F159-4107-2B4F-1D4A4E15B886}";

/** Build a minimal notesSlide part: slide-image, body, and slide-number placeholders (the shape PowerPoint expects — see buildInitialNotesSlideRelsXml for why the slide-number placeholder matters). */
function buildNotesSlideXml(text: string): string {
  const paragraphs = text
    .split("\n")
    .map((line) => `<a:p><a:r><a:rPr lang="en-US" dirty="0"/><a:t>${escapeXmlText(line)}</a:t></a:r></a:p>`)
    .join("");

  return (
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<p:notes xmlns:a="${NS_A}" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" xmlns:p="${NS_P}">` +
    `<p:cSld><p:spTree>` +
    `<p:nvGrpSpPr><p:cNvPr id="1" name=""/><p:cNvGrpSpPr/><p:nvPr/></p:nvGrpSpPr>` +
    `<p:grpSpPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="0" cy="0"/><a:chOff x="0" y="0"/><a:chExt cx="0" cy="0"/></a:xfrm></p:grpSpPr>` +
    `<p:sp><p:nvSpPr><p:cNvPr id="2" name="Slide Image Placeholder 1"/>` +
    `<p:cNvSpPr><a:spLocks noGrp="1" noRot="1" noChangeAspect="1"/></p:cNvSpPr>` +
    `<p:nvPr><p:ph type="sldImg"/></p:nvPr></p:nvSpPr><p:spPr/></p:sp>` +
    `<p:sp><p:nvSpPr><p:cNvPr id="3" name="Notes Placeholder 2"/>` +
    `<p:cNvSpPr><a:spLocks noGrp="1"/></p:cNvSpPr>` +
    `<p:nvPr><p:ph type="body" idx="1"/></p:nvPr></p:nvSpPr>` +
    `<p:spPr/><p:txBody><a:bodyPr/><a:lstStyle/>${paragraphs}</p:txBody></p:sp>` +
    `<p:sp><p:nvSpPr><p:cNvPr id="4" name="Slide Number Placeholder 3"/>` +
    `<p:cNvSpPr><a:spLocks noGrp="1"/></p:cNvSpPr>` +
    `<p:nvPr><p:ph type="sldNum" sz="quarter" idx="5"/></p:nvPr></p:nvSpPr>` +
    `<p:spPr/><p:txBody><a:bodyPr/><a:lstStyle/><a:p><a:fld id="${SLIDE_NUM_FIELD_ID}" type="slidenum">` +
    `<a:rPr lang="en-US"/><a:t></a:t></a:fld><a:endParaRPr lang="en-US"/></a:p></p:txBody></p:sp>` +
    `</p:spTree></p:cSld></p:notes>`
  );
}

const NOTES_SLIDE_DIR = "ppt/notesSlides";
const NOTES_SLIDE_PART_PATH = `${NOTES_SLIDE_DIR}/notesSlide1.xml`;
const NOTES_SLIDE_RELS_PATH = `${NOTES_SLIDE_DIR}/_rels/notesSlide1.xml.rels`;
const CONTENT_TYPES_PATH = "[Content_Types].xml";
const OOXML_RELATIONSHIPS_NS = "http://schemas.openxmlformats.org/package/2006/relationships";
const OOXML_CONTENT_TYPES_NS = "http://schemas.openxmlformats.org/package/2006/content-types";
const SLIDE_RELATIONSHIP_TYPE = "http://schemas.openxmlformats.org/officeDocument/2006/relationships/slide";
const NOTES_SLIDE_RELATIONSHIP_TYPE = `http://schemas.openxmlformats.org/officeDocument/2006/relationships${NOTES_SLIDE_RELATIONSHIP_TYPE_SUFFIX}`;
const NOTES_SLIDE_CONTENT_TYPE = "application/vnd.openxmlformats-officedocument.presentationml.notesSlide+xml";
const NOTES_MASTER_RELATIONSHIP_TYPE = "http://schemas.openxmlformats.org/officeDocument/2006/relationships/notesMaster";
// Every real PowerPoint deck has exactly one notesMaster (auto-created the
// first time any slide ever gets notes), so insertSlidesFromBase64 resolves
// this against the DESTINATION presentation's own notesMaster even though
// the single-slide export we're rewriting never carries that part itself.
// Without this relationship, PowerPoint rejects the freshly-built notesSlide
// with RichApi.Error: InvalidArgument on insert.
const NOTES_MASTER_RELATIONSHIP_TARGET = "../notesMasters/notesMaster1.xml";

/** The notesSlide1.xml.rels part linking a freshly-created notesSlide back to slide1.xml and the deck's notesMaster. */
function buildInitialNotesSlideRelsXml(): string {
  return (
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Relationships xmlns="${OOXML_RELATIONSHIPS_NS}">` +
    `<Relationship Id="rId1" Type="${NOTES_MASTER_RELATIONSHIP_TYPE}" Target="${NOTES_MASTER_RELATIONSHIP_TARGET}"/>` +
    `<Relationship Id="rId2" Type="${SLIDE_RELATIONSHIP_TYPE}" Target="../slides/slide1.xml"/>` +
    `</Relationships>`
  );
}

/**
 * Picks the next free rId for a new relationship. Relationship ids must be
 * unique within the .rels part, so this scans every existing "rId<n>" and
 * uses one past the *highest* — not the count — since ids can have gaps
 * (e.g. a previously deleted relationship) or appear out of numeric order.
 */
function nextRelationshipId(relsXml: string | null): string {
  const existingIds = [...(relsXml ?? "").matchAll(/rId(\d+)/g)].map((m) => Number.parseInt(m[1] ?? "0", 10));
  const highestExistingId = existingIds.length > 0 ? Math.max(...existingIds) : 0;
  return `rId${highestExistingId + 1}`;
}

const NOTES_SLIDE_RELATIONSHIP_TARGET = "../notesSlides/notesSlide1.xml";

/** Build slide1.xml.rels content with a new notesSlide relationship added to it. */
function addNotesSlideRelationship(relsXml: string | null): string {
  const rId = nextRelationshipId(relsXml);

  if (!relsXml) {
    return (
      `${XML_PROLOG}` +
      `<Relationships xmlns="${OOXML_RELATIONSHIPS_NS}">` +
      `<Relationship Id="${rId}" Type="${NOTES_SLIDE_RELATIONSHIP_TYPE}" Target="${NOTES_SLIDE_RELATIONSHIP_TARGET}"/>` +
      `</Relationships>`
    );
  }

  const relsDoc = parseXmlForEditing(relsXml, "notes-rels-update", "slide1.xml.rels");
  if (relsDoc) {
    const relationship = relsDoc.createElementNS(OOXML_RELATIONSHIPS_NS, "Relationship");
    relationship.setAttribute("Id", rId);
    relationship.setAttribute("Type", NOTES_SLIDE_RELATIONSHIP_TYPE);
    relationship.setAttribute("Target", NOTES_SLIDE_RELATIONSHIP_TARGET);
    relsDoc.documentElement.appendChild(relationship);
    return serializeXmlDocument(relsDoc);
  }

  // Couldn't parse the existing rels XML at all (already logged by
  // parseXmlForEditing) — fall back to text splicing so the notesSlide
  // relationship still isn't silently dropped, even though the result may
  // still carry whatever defect the source XML already had.
  const notesRel = `<Relationship Id="${rId}" Type="${NOTES_SLIDE_RELATIONSHIP_TYPE}" Target="${NOTES_SLIDE_RELATIONSHIP_TARGET}"/>`;
  const spliced = relsXml.replace("</Relationships>", `${notesRel}</Relationships>`);
  return spliced === relsXml ? `${relsXml}${notesRel}` : spliced;
}

/** Declare the notesSlide content type in [Content_Types].xml if it isn't already there. */
function ensureNotesSlideContentType(files: PptxZip): void {
  const ctXml = readZipText(files, CONTENT_TYPES_PATH);
  if (!ctXml) {
    // [Content_Types].xml is mandatory in every OOXML package — missing it
    // means the export itself is malformed, not that there's nothing to do.
    logPptFailure(
      "notes-content-types",
      new Error(`${CONTENT_TYPES_PATH} is missing from the exported slide`),
    );
    return;
  }
  if (ctXml.includes("notesSlide")) return;

  const ctDoc = parseXmlForEditing(ctXml, "notes-content-types", CONTENT_TYPES_PATH);
  if (ctDoc) {
    const override = ctDoc.createElementNS(OOXML_CONTENT_TYPES_NS, "Override");
    override.setAttribute("PartName", `/${NOTES_SLIDE_PART_PATH}`);
    override.setAttribute("ContentType", NOTES_SLIDE_CONTENT_TYPE);
    ctDoc.documentElement.appendChild(override);
    files[CONTENT_TYPES_PATH] = strToU8(serializeXmlDocument(ctDoc));
    return;
  }

  // Couldn't parse [Content_Types].xml at all (already logged above) — fall
  // back to text splicing rather than silently skipping the declaration.
  const overrideTag = `<Override PartName="/${NOTES_SLIDE_PART_PATH}" ContentType="${NOTES_SLIDE_CONTENT_TYPE}"/></Types>`;
  const spliced = ctXml.replace("</Types>", overrideTag);
  if (spliced !== ctXml) {
    files[CONTENT_TYPES_PATH] = strToU8(spliced);
  }
}

/**
 * Write `text` as the slide's notes, updating an existing notesSlide part or
 * wiring up a new one. Exported so brand-check's "Notes present" fix can
 * clear notes (pass "") by reusing this proven read/write path instead of
 * hand-rolling a second notesSlide XML writer.
 */
export function writeNotesText(files: PptxZip, text: string): void {
  const notesXml = strToU8(buildNotesSlideXml(text));
  const relsXml = readZipText(files, SLIDE1_RELS_PATH);
  const existingNotesPath = resolveNotesPath(relsXml);

  if (existingNotesPath) {
    files[existingNotesPath] = notesXml;
    return;
  }

  files[NOTES_SLIDE_PART_PATH] = notesXml;
  files[NOTES_SLIDE_RELS_PATH] = strToU8(buildInitialNotesSlideRelsXml());
  files[SLIDE1_RELS_PATH] = strToU8(addNotesSlideRelationship(relsXml));
  ensureNotesSlideContentType(files);
}

/** Export the selected slide and unzip it, or null if nothing is selected. */
async function exportSelectedSlide(
  ctx: PowerPoint.RequestContext,
): Promise<{ slideId: string; files: PptxZip } | null> {
  const slide = await getSelectedSlide(ctx, "items/id");
  if (!slide) return null;

  const exportResult = slide.exportAsBase64();
  await ctx.sync();
  const files = await unzipPptxBase64(exportResult.value);
  return { slideId: slide.id, files };
}

export async function getSpeakerNotes(): Promise<string> {
  return runPpt("", async (ctx) => {
    const exported = await exportSelectedSlide(ctx);
    return exported ? extractNotesText(exported.files) : "";
  });
}

export async function setSpeakerNotes(
  notesText: string,
  mode: "append" | "replace" = "append",
): Promise<boolean> {
  if (!hasPpt()) return false;
  const trimmed = notesText.trim();
  if (!trimmed) return false;
  try {
    let applied = false;
    await ppt().run(async (ctx) => {
      const exported = await exportSelectedSlide(ctx);
      if (!exported) throw new Error("No slide selected");
      const { slideId, files } = exported;

      const existing = mode === "append" ? extractNotesText(files) : "";
      const combined = existing ? `${existing.trimEnd()}\n\n${trimmed}` : trimmed;
      writeNotesText(files, combined);

      await replaceSlideWithRewrittenCopy(
        ctx,
        files,
        slideId,
        "KeepSourceFormatting" as PowerPoint.InsertSlideFormatting,
      );
      applied = true;
    });
    return applied;
  } catch (e) {
    return logPptFailure("notes", e);
  }
}
